import {createTheme} from '@material-ui/core/styles';
import {Palette} from '@material-ui/core/styles/createPalette';

const theme = createTheme();

declare module '@material-ui/core/styles/createTheme' {
  interface Theme {
    elevation: {
      aboveModalWindow: number;
    };
  }

  interface ThemeOptions {
    elevation?: {
      aboveModalWindow?: number;
    };
  }
}

declare module '@material-ui/core/styles/createMixins' {
  interface Mixins {
    header: {
      height: number;
    };
  }

  interface MixinsOptions {
    header?: {
      height?: number;
    };
  }
}

// copied from user-tooltip (see UserAvatar.styl). The arrow style there looks bad here (our arrow is smaller).
// This looks...ok...on the arrow, too. Could probably use some improvement.
const tooltipBoxShadow = '0px 0px 8px rgba(0, 0, 0, 0.298039)';

export const common = (palette: Partial<Palette>) =>
  createTheme({
    shadows: [...(theme.shadows.slice(0, 24) as any), '0px 4px 20px rgba(0, 0, 0, 0.15)'] as any,
    typography: {
      fontFamily: '"Roboto", sans-serif',
      body1: {
        fontSize: 14,
        lineHeight: '14px',
      },
      h1: {
        fontSize: 28,
        lineHeight: '28px',
        fontFamily: '"Poppins", sans-serif',
      },
      h2: {
        fontSize: 20,
        lineHeight: '20px',
        fontFamily: '"Poppins", sans-serif',
      },
      h3: {
        fontSize: 16,
        lineHeight: '16px',
        fontFamily: '"Poppins", sans-serif',
      },
      h4: {
        fontSize: 14,
        lineHeight: '14px',
        fontFamily: '"Poppins", sans-serif',
        fontWeight: 'bold',
      },
      h5: {
        fontSize: 12,
        fontFamily: '"Poppins", sans-serif',
      },
      h6: {
        color: '#808080',
        fontSize: 12,
        fontWeight: 700,
        lineHeight: '14px',
        fontFamily: '"Poppins", sans-serif',
      },
    },
    shape: {
      borderRadius: 4,
    },
    palette: {
      ...theme.palette,
      ...palette,
    },
    elevation: {
      // Must be bigger than modal window z-index which is 1500
      aboveModalWindow: 1501,
    },
    overrides: {
      MuiButton: {
        label: {
          whiteSpace: 'nowrap',
        },
        sizeSmall: {
          minWidth: 50,
        },
        iconSizeSmall: {},
        startIcon: {
          '&$iconSizeSmall': {
            marginLeft: -4,
          },
        },
        text: {
          minHeight: 32,
          padding: '3px 8px',
          fontSize: 14,
          fontWeight: 500,
        },
        textSizeSmall: {
          minHeight: 24,
          padding: '0 8px',
        },
        root: {
          minHeight: 32,
          padding: '5px 16px',
          textTransform: 'none',
          borderRadius: 40,
          fontFamily: 'Roboto',
          fontWeight: 600,
        },
        outlinedSizeSmall: {
          fontFamily: 'Roboto, sans-serif',
          fontSize: 12,
          borderRadius: 24,
          padding: '1px 7px',
          lineHeight: '1.7',
          minHeight: 24,
        },
        textSizeLarge: {
          fontSize: 16,
        },
        contained: {
          fontSize: 14,
          lineHeight: '1',
          borderRadius: 40,
        },
      },
      MuiCheckbox: {
        root: {
          color: 'black', // outline color is gray by default.
          padding: 0, // inherits IconButton's 12px padding, which we do not want.
        },
      },
      MuiDialogActions: {
        root: {
          padding: 16,
        },
      },
      MuiDialogTitle: {
        root: {
          boxSizing: 'border-box',
          minHeight: 52,
          padding: '12px 24px',
        },
      },
      MuiFormControlLabel: {
        labelPlacementStart: {
          marginLeft: 0,
          '& > span': {
            marginRight: 10,
            width: 134,
          },
        },
      },
      MuiIconButton: {
        root: {
          fontSize: 12,
        },
      },
      MuiInputBase: {
        input: {
          fontSize: 14,
        },
      },
      MuiInputLabel: {
        outlined: {
          transform: 'translate(10px, 9px) scale(1)',
          '&$marginDense': {
            transform: 'translate(10px, 9px) scale(1)',
          },
        },
        marginDense: {},
      },
      MuiList: {
        padding: {
          paddingLeft: 10,
          paddingRight: 10,
        },
      },
      MuiMenuItem: {
        root: {
          boxSizing: 'border-box',
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          '&:not(:last-child)': {
            borderBottom: `1px solid ${palette.divider}`,
          },
          [theme.breakpoints.up('sm')]: {
            '&:not(:last-child)': {
              borderBottom: `1px solid ${palette.divider}`,
            },
          },
        },
      },
      MuiOutlinedInput: {
        input: {
          padding: '7.7px 12px',
        },
      },
      MuiPopover: {
        root: {
          // Must be like this because of old elements
          zIndex: `1500!important` as any,
        },
      },
      MuiSelect: {
        iconOutlined: {
          right: 10,
        },
      },
      MuiSvgIcon: {
        fontSizeSmall: {
          fontSize: 20,
        },
        root: {
          fontSize: 24,
        },
      },
      MuiTab: {
        root: {
          fontSize: 14,
          textTransform: 'none',
        },
      },
      MuiTooltip: {
        tooltip: {
          fontSize: '12px',
          backgroundColor: 'white',
          color: 'black',
          boxShadow: tooltipBoxShadow,
        },
        arrow: {
          color: 'white',
          '&::before': {
            boxShadow: tooltipBoxShadow,
          },
        },
      },
      MuiSwitch: {
        switchBase: {
          color: '#808080',
        },
        track: {
          backgroundColor: '#C4C4C4',
        },
      },
      MuiCard: {
        root: {
          border: '1px solid #CCC',
          boxShadow: '0 2px 2px 0 rgba(0,0,0,0.1)',
        },
      },
      MuiTableCell: {
        root: {
          padding: '12px',
          fontSize: '14px',
        },
      },
    },
    props: {
      MuiButton: {
        disableRipple: true,
      },
      MuiButtonBase: {
        disableRipple: true,
      },
      MuiCheckbox: {
        disableRipple: true,
        color: 'primary', // MUI uses the secondary color by default, but we almost always want primary.
      },
      MuiRadio: {
        color: 'default',
      },
      MuiSelect: {
        variant: 'outlined',
      },
      MuiSwitch: {
        size: 'small',
        color: 'primary',
      },
      MuiTab: {
        disableFocusRipple: true,
      },
    },
    mixins: {
      header: {
        height: 48,
      },
    },
  });
